import type { AxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders } from "axios";
import api from "../lib/axios";
import { BaseResponse } from "../lib/api-contract";
import { createApiResultError } from "../lib/result-codes";

type RequestConfig = Omit<AxiosRequestConfig, "data" | "method" | "url">;

async function request<T>(config: AxiosRequestConfig): Promise<BaseResponse<T>> {
  const response: AxiosResponse<BaseResponse<T>> = await api.request<BaseResponse<T>>(config);

  return response.data;
}

async function requestData<T>(config: AxiosRequestConfig, fallbackMessage?: string): Promise<T> {
  return unwrap(await request<T>(config), fallbackMessage);
}

function unwrap<T>(response: BaseResponse<T>, fallbackMessage = "Request failed"): T {
  if (!response.success || response.data === null) {
    throw createApiResultError(response, fallbackMessage);
  }

  return response.data;
}

export const apiUtils = {
  unwrap,

  getData<T>(url: string, fallbackMessage?: string, config?: RequestConfig): Promise<T> {
    return requestData<T>({ ...config, method: "GET", url }, fallbackMessage);
  },

  postData<T, TBody = unknown>(
    url: string,
    data?: TBody,
    fallbackMessage?: string,
    config?: RequestConfig,
  ): Promise<T> {
    return requestData<T>({ ...config, method: "POST", url, data }, fallbackMessage);
  },

  putData<T, TBody = unknown>(
    url: string,
    data?: TBody,
    fallbackMessage?: string,
    config?: RequestConfig,
  ): Promise<T> {
    return requestData<T>({ ...config, method: "PUT", url, data }, fallbackMessage);
  },

  patchData<T, TBody = unknown>(
    url: string,
    data?: TBody,
    fallbackMessage?: string,
    config?: RequestConfig,
  ): Promise<T> {
    return requestData<T>({ ...config, method: "PATCH", url, data }, fallbackMessage);
  },
  get<T>(url: string, config?: RequestConfig): Promise<BaseResponse<T>> {
    return request<T>({ ...config, method: "GET", url });
  },

  post<T, TBody = unknown>(
    url: string,
    data?: TBody,
    config?: RequestConfig,
  ): Promise<BaseResponse<T>> {
    return request<T>({ ...config, method: "POST", url, data });
  },

  put<T, TBody = unknown>(
    url: string,
    data?: TBody,
    config?: RequestConfig,
  ): Promise<BaseResponse<T>> {
    return request<T>({ ...config, method: "PUT", url, data });
  },

  patch<T, TBody = unknown>(
    url: string,
    data?: TBody,
    config?: RequestConfig,
  ): Promise<BaseResponse<T>> {
    return request<T>({ ...config, method: "PATCH", url, data });
  },

  delete<T>(url: string, config?: RequestConfig): Promise<BaseResponse<T>> {
    return request<T>({ ...config, method: "DELETE", url });
  },

  postWithJson<T, TBody = unknown>(
    url: string,
    data?: TBody,
    config?: RequestConfig,
  ): Promise<BaseResponse<T>> {
    return request<T>({
      ...config,
      method: "POST",
      url,
      data,
      headers: {
        "Content-Type": "application/json",
        ...(config?.headers as RawAxiosRequestHeaders | undefined),
      },
    });
  },

  postWithFormData<T>(
    url: string,
    data: FormData,
    config?: RequestConfig,
  ): Promise<BaseResponse<T>> {
    return request<T>({
      ...config,
      method: "POST",
      url,
      data,
      headers: {
        ...(config?.headers as RawAxiosRequestHeaders | undefined),
        // Let the browser set the multipart boundary instead of inheriting JSON.
        "Content-Type": false,
      },
    });
  },

  putWithFormData<T>(
    url: string,
    data: FormData,
    config?: RequestConfig,
  ): Promise<BaseResponse<T>> {
    return request<T>({
      ...config,
      method: "PUT",
      url,
      data,
      headers: {
        ...(config?.headers as RawAxiosRequestHeaders | undefined),
        // Let the browser set the multipart boundary instead of inheriting JSON.
        "Content-Type": false,
      },
    });
  },

  patchWithFormData<T>(
    url: string,
    data: FormData,
    config?: RequestConfig,
  ): Promise<BaseResponse<T>> {
    return request<T>({
      ...config,
      method: "PATCH",
      url,
      data,
      headers: {
        ...(config?.headers as RawAxiosRequestHeaders | undefined),
        // Let the browser set the multipart boundary instead of inheriting JSON.
        "Content-Type": false,
      },
    });
  },
};

export default apiUtils;
